import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { WidgetId } from '@/types/analytics';
import styles from './WidgetCustomizer.module.scss';

export interface WidgetCustomizerProps {
  isOpen: boolean;
  onClose: () => void;
  enabledWidgets: WidgetId[];
  onSave: (selectedWidgets: WidgetId[]) => void;
}

const AVAILABLE_WIDGETS: { id: WidgetId; name: string; category: string }[] = [
  // Performance
  { id: 'overall-success-rate', name: 'Overall Success Rate', category: 'Performance' },
  { id: 'performance-by-difficulty', name: 'Performance by Difficulty', category: 'Performance' },
  { id: 'performance-by-type', name: 'Performance by Type', category: 'Performance' },
  { id: 'improvement-trends', name: 'Improvement Trends', category: 'Performance' },
  { id: 'time-efficiency', name: 'Time Efficiency', category: 'Performance' },
  { id: 'partial-credit-analysis', name: 'Partial Credit', category: 'Performance' },

  // Questions
  { id: 'most-challenging', name: 'Most Challenging', category: 'Questions' },
  { id: 'questions-mastered', name: 'Questions Mastered', category: 'Questions' },
  { id: 'topic-mastery', name: 'Topic Mastery', category: 'Questions' },

  // Engagement
  { id: 'daily-knowledge-streak', name: 'Daily Knowledge Streak', category: 'Engagement' },
  { id: 'calendar-heatmap', name: 'Activity Calendar', category: 'Engagement' },
  { id: 'total-questions-answered', name: 'Total Questions', category: 'Engagement' },
  { id: 'total-study-time', name: 'Total Study Time', category: 'Engagement' },
  { id: 'sessions-completed', name: 'Sessions Completed', category: 'Engagement' },
  { id: 'session-completion-rate', name: 'Session Completion Rate', category: 'Engagement' },
  { id: 'time-distribution', name: 'Time Distribution', category: 'Engagement' },
  { id: 'participation-trends', name: 'Participation Trends', category: 'Engagement' },

  // Quizlets (General)
  { id: 'quizlet-mastery-heatmap', name: 'Quizlet Mastery Heatmap', category: 'Quizlets' },
  { id: 'best-worst-performing', name: 'Best/Worst Performing', category: 'Quizlets' },

  // Insights (AI)
  { id: 'automated-insights', name: 'Key Insights', category: 'AI Insights' },
  { id: 'practice-recommendations', name: 'Practice Recommendations', category: 'AI Insights' },
  { id: 'learning-curve', name: 'Learning Curve', category: 'AI Insights' },
  { id: 'best-study-time', name: 'Best Study Time', category: 'AI Insights' },
  { id: 'time-optimization', name: 'Time Optimization', category: 'AI Insights' },

  // Articles
  { id: 'article-stats', name: 'Article Stats', category: 'Articles' },
  { id: 'article-views-chart', name: 'Article Views', category: 'Articles' },
  { id: 'top-articles', name: 'Top Performing Articles', category: 'Articles' },
];

export function WidgetCustomizer({
  isOpen,
  onClose,
  enabledWidgets,
  onSave,
}: WidgetCustomizerProps) {
  const [selectedWidgets, setSelectedWidgets] = useState<WidgetId[]>(enabledWidgets);

  const toggleWidget = (widgetId: WidgetId) => {
    setSelectedWidgets((prev) =>
      prev.includes(widgetId)
        ? prev.filter((id) => id !== widgetId)
        : [...prev, widgetId]
    );
  };

  const handleSave = () => {
    onSave(selectedWidgets);
    onClose();
  };

  const categories = [...new Set(AVAILABLE_WIDGETS.map((w) => w.category))];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className={styles.customizer__backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className={styles.customizer__modal}
            initial={{ opacity: 0, scale: 0.95, x: '-50%', y: '-50%' }}
            animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
            exit={{ opacity: 0, scale: 0.95, x: '-50%', y: '-50%' }}
          >
            <div className={styles.customizer__header}>
              <h2 className={styles.customizer__title}>Customize Dashboard</h2>
              <button className={styles.customizer__close} onClick={onClose}>
                ×
              </button>
            </div>

            <div className={styles.customizer__content}>
              {categories.map((category) => (
                <div key={category} className={styles.customizer__category}>
                  <h3 className={styles.customizer__categoryTitle}>{category}</h3>
                  <div className={styles.customizer__widgets}>
                    {AVAILABLE_WIDGETS.filter((w) => w.category === category).map(
                      (widget) => (
                        <label
                          key={widget.id}
                          className={styles.customizer__widgetLabel}
                        >
                          <input
                            type="checkbox"
                            checked={selectedWidgets.includes(widget.id)}
                            onChange={() => toggleWidget(widget.id)}
                            className={styles.customizer__checkbox}
                          />
                          <span className={styles.customizer__widgetName}>
                            {widget.name}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.customizer__footer}>
              <button className={styles.customizer__cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button className={styles.customizer__saveButton} onClick={handleSave}>
                Save Changes
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
